<template lang="pug">
el-dialog(
    :title="modalTitle"
    :visible="visible"
    width="800px"
    :before-close="handleClose"
)
    .url-clicks-content

        //- URL Stats View
        template(v-if="currentView === 'urls'")
            .url-stats-view(v-loading="loading")
                template(v-if="items && items.length")
                    el-table(
                        :data="sortedItems"
                        stripe
                        size="small"
                        :default-sort="{ prop: 'total_clicks', order: 'descending' }"
                        @sort-change="handleSortChange"
                    )
                        el-table-column(
                            prop="url"
                            :label="$t('URL')"
                            min-width="300"
                        )
                            template(slot-scope="scope")
                                el-tooltip(
                                    :content="cleanUrl(scope.row.url)"
                                    placement="top"
                                    :open-delay="500"
                                )
                                    a.url-link(:href="cleanUrl(scope.row.url)" target="_blank" rel="noopener")
                                        span.url-text {{ cleanUrl(scope.row.url) }}
                                        i.el-icon-link
                        el-table-column(
                            prop="total_clicks"
                            :label="$t('Total Clicks')"
                            sortable="custom"
                            width="120"
                            align="center"
                        )
                            template(slot-scope="scope")
                                span.click-count {{ scope.row.total_clicks }}
                        el-table-column(
                            prop="unique_clickers"
                            :label="$t('Unique Clickers')"
                            sortable="custom"
                            width="140"
                            align="center"
                        )
                            template(slot-scope="scope")
                                .clicker-cell(@click="showClickers(scope.row)")
                                    span.clicker-count {{ scope.row.unique_clickers }}
                                    i.el-icon-fa-user-friends.fas.clicker-icon

                    //- Summary
                    .summary
                        .summary-item
                            .summary-label {{ $t('Total URLs') }}:
                            .summary-value {{ items.length }}
                        .summary-item
                            .summary-label {{ $t('Total Clicks') }}:
                            .summary-value {{ totalClicks }}
                        .summary-item
                            .summary-label {{ $t('Unique Clickers') }}:
                            .summary-value {{ totalUniqueClickers }}

                .empty-state(v-else-if="!loading")
                    i.el-icon-warning-outline
                    p {{ $t('No click data available yet.') }}

        //- Clickers List View
        template(v-if="currentView === 'clickers'")
            .clickers-view(v-loading="clickersLoading")
                //- URL info header
                .clickers-url-header
                    span.label {{ $t('Clickers for') }}:
                    el-tooltip(:content="cleanUrl(selectedUrl)" placement="top" :open-delay="500")
                        span.url {{ truncateUrl(cleanUrl(selectedUrl)) }}

                template(v-if="clickerItems && clickerItems.length")
                    el-table(
                        :data="clickerItems"
                        stripe
                        size="small"
                    )
                        el-table-column(
                            prop="recipient_name"
                            :label="$t('Name')"
                            min-width="180"
                        )
                            template(slot-scope="scope")
                                span {{ formatName(scope.row) }}
                        el-table-column(
                            prop="recipient_email"
                            :label="$t('Email')"
                            min-width="220"
                        )
                        el-table-column(
                            prop="clicked_at"
                            :label="$t('Click Time')"
                            width="180"
                        )
                            template(slot-scope="scope")
                                span {{ formatDateTime(scope.row.clicked_at) }}

                    //- Pagination
                    .pagination-footer
                        el-pagination(
                            background
                            layout="prev, pager, next"
                            :total="clickersPageInfo.total"
                            :page-size="clickersPageInfo.perPage"
                            :current-page="clickersPageInfo.currentPage"
                            @current-change="handleClickersPageChange"
                        )

                .empty-state(v-else-if="!clickersLoading")
                    i.el-icon-warning-outline
                    p {{ $t('No clickers found for this URL.') }}

    //- Footer with back button and tag button (shown when viewing clickers)
    .dialog-footer-spread(slot="footer" v-if="currentView === 'clickers'")
        el-button(
            icon="el-icon-arrow-left"
            @click="backToUrlList"
        ) {{ $t('Back to URLs') }}
        el-button(
            type="primary"
            icon="el-icon-price-tag"
            @click="openBulkTagDialog"
            :disabled="!clickerItems || clickerItems.length === 0"
        ) {{ $t('Tag all clickers') }}

    //- Bulk Tag Dialog
    el-dialog(
        :title="$t('Add tags to clickers')"
        :visible.sync="bulkTagDialogVisible"
        width="500px"
        append-to-body
    )
        .bulk-tag-content
            el-form
                el-form-item(:label="$t('Select tags')")
                    el-select(
                        ref="bulkTagSelect"
                        v-model="selectedTagsModel"
                        multiple
                        filterable
                        allow-create
                        default-first-option
                        value-key="id"
                        :placeholder="$t('Select tags')"
                        style="width: 100%"
                        :loading="tagOptionsLoading"
                        @change="$refs.bulkTagSelect.blur()"
                    )
                        el-option(
                            v-for="tag in allTagOptions"
                            :key="tag.id"
                            :label="tag.name"
                            :value="tag"
                        )
        span.dialog-footer(slot="footer")
            el-button(@click="bulkTagDialogVisible = false") {{ $t('Cancel') }}
            el-button(
                type="primary"
                @click="applyBulkTags"
                :loading="bulkTaggingLoading"
                :disabled="!selectedTags || selectedTags.length === 0"
            ) {{ $t('Apply tags') }}
</template>

<i18n>
{
    "de": {
        "Click Statistics": "Klick-Statistiken",
        "URL": "URL",
        "Total Clicks": "Klicks gesamt",
        "Unique Clickers": "Eindeutige Klicker",
        "Total URLs": "URLs gesamt",
        "No click data available yet.": "Noch keine Klickdaten verfügbar.",
        "URL Clickers": "URL-Klicker",
        "Back to URLs": "Zurück zu URLs",
        "Tag all clickers": "Alle Klicker taggen",
        "Clickers for": "Klicker für",
        "Name": "Name",
        "Email": "E-Mail",
        "Click Time": "Klickzeit",
        "Add tags to clickers": "Tags zu Klickern hinzufügen",
        "Select tags": "Tags auswaehlen",
        "Apply tags": "Tags anwenden",
        "Tags applied successfully": "Tags erfolgreich angewendet",
        "No clickers found for this URL.": "Keine Klicker für diese URL gefunden.",
        "Cancel": "Abbrechen"
    }
}
</i18n>

<script>
import _ from 'lodash'
import uniqid from 'uniqid'
import { useNewsletterNewsletterStore, useSetupStore } from '@/stores'
import MemberTagApi from '@/extensions/newsletter/store/api/memberTag'
import { timestamp2date } from '@/filters'

export default {
    name: 'UrlClicksModal',
    props: {
        newsletterId: {
            type: [Number, String],
            default: null
        }
    },
    data: function() {
        return {
            sortProp: 'total_clicks',
            sortOrder: 'descending',
            currentView: 'urls',
            selectedUrl: null,
            bulkTagDialogVisible: false,
            selectedTags: [],
            tagOptions: [],
            tagOptionsLoading: false
        }
    },
    computed: {
        newsletterStore: function() {
            return useNewsletterNewsletterStore()
        },
        setupStore: function() {
            return useSetupStore()
        },
        urlClicks: function() {
            return this.newsletterStore.urlClicks
        },
        urlClickers: function() {
            return this.newsletterStore.urlClickers
        },
        visible: function() {
            return _.get(this.urlClicks, 'visible', false)
        },
        loading: function() {
            return _.get(this.urlClicks, 'loading', false)
        },
        items: function() {
            return _.get(this.urlClicks, 'items', [])
        },
        sortedItems: function() {
            var self = this
            if (!this.items || !this.items.length) return []
            var itemsCopy = this.items.slice()
            var direction = this.sortOrder === 'ascending' ? 1 : -1
            return itemsCopy.sort(function(a, b) {
                var aVal = a[self.sortProp] || 0
                var bVal = b[self.sortProp] || 0
                return (aVal - bVal) * direction
            })
        },
        totalClicks: function() {
            return this.items.reduce(function(sum, item) {
                return sum + (item.total_clicks || 0)
            }, 0)
        },
        totalUniqueClickers: function() {
            return this.items.reduce(function(sum, item) {
                return sum + (item.unique_clickers || 0)
            }, 0)
        },
        clickerItems: function() {
            return _.get(this.urlClickers, 'items', [])
        },
        clickersLoading: function() {
            return _.get(this.urlClickers, 'loading', false)
        },
        clickersPageInfo: function() {
            return _.get(this.urlClickers, 'pageInfo', {
                total: 0,
                perPage: 12,
                currentPage: 1,
                lastPage: 0
            })
        },
        bulkTaggingLoading: function() {
            return _.get(this.newsletterStore, 'bulkTagging.loading', false)
        },
        modalTitle: function() {
            if (this.currentView === 'clickers') {
                return this.$t('URL Clickers')
            }
            return this.$t('Click Statistics')
        },
        activeCmsId: function() {
            return _.get(this.setupStore, 'activeCms.id', null)
        },
        allTagOptions: function() {
            var existingOptions = this.tagOptions || []
            var newTags = this.selectedTags.filter(function(tag) {
                return tag.id && String(tag.id).startsWith('new:')
            })
            return existingOptions.concat(newTags)
        },
        selectedTagsModel: {
            get: function() {
                var self = this
                return this.selectedTags.map(function(tag) {
                    if (tag.id && String(tag.id).startsWith('new:')) {
                        return tag.name
                    }
                    return tag
                })
            },
            set: function(value) {
                var self = this
                var tags = []
                value.forEach(function(tag) {
                    if (typeof tag === 'string' || tag instanceof String) {
                        tags.push({
                            id: uniqid('new:'),
                            name: tag
                        })
                    } else {
                        tags.push(tag)
                    }
                })
                self.selectedTags = tags
            }
        }
    },
    watch: {
        visible: function(newValue) {
            if (!newValue) {
                this.currentView = 'urls'
                this.selectedUrl = null
                this.selectedTags = []
            }
        }
    },
    methods: {
        closeUrlClicks: function() {
            this.newsletterStore.closeUrlClicks()
        },
        handleClose: function() {
            this.closeUrlClicks()
        },
        handleSortChange: function(sortInfo) {
            this.sortProp = sortInfo.prop
            this.sortOrder = sortInfo.order
        },
        cleanUrl: function(url) {
            try {
                var parsed = new URL(url)
                parsed.searchParams.delete('marketing-campaign')
                return parsed.toString()
            } catch (e) {
                return url
            }
        },
        truncateUrl: function(url) {
            if (url && url.length > 60) {
                return url.substring(0, 60) + '...'
            }
            return url
        },
        showClickers: function(row) {
            this.selectedUrl = row.url
            this.currentView = 'clickers'
            this.fetchClickers(1)
        },
        fetchClickers: function(page) {
            var newsletterId = this.newsletterId || _.get(this.newsletterStore, 'form.id')
            if (!newsletterId || !this.selectedUrl) return

            this.newsletterStore.fetchUrlClickers({
                newsletterId: newsletterId,
                url: this.selectedUrl,
                currentPage: page,
                perPage: 12
            })
        },
        backToUrlList: function() {
            this.currentView = 'urls'
            this.selectedUrl = null
            this.newsletterStore.resetUrlClickers()
        },
        handleClickersPageChange: function(page) {
            this.fetchClickers(page)
        },
        formatName: function(row) {
            var name = row.recipient_name
            if (name) return name
            return '-'
        },
        formatDateTime: function(timestamp) {
            if (!timestamp) return '-'
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm')
        },
        openBulkTagDialog: function() {
            this.selectedTags = []
            this.loadTagOptions()
            this.bulkTagDialogVisible = true
        },
        loadTagOptions: function() {
            var self = this
            if (!this.activeCmsId) return

            this.tagOptionsLoading = true
            MemberTagApi.fetchList(this.activeCmsId)
                .then(function(tags) {
                    self.tagOptions = tags || []
                    self.tagOptionsLoading = false
                })
                .catch(function() {
                    self.tagOptions = []
                    self.tagOptionsLoading = false
                })
        },
        applyBulkTags: function() {
            var self = this
            if (!this.selectedTags || this.selectedTags.length === 0) return
            if (!this.activeCmsId) return

            var emails = this.clickerItems.map(function(item) {
                return item.recipient_email
            }).filter(function(email) {
                return email
            })

            if (emails.length === 0) {
                this.$message.warning(this.$t('No valid emails found'))
                return
            }

            this.newsletterStore.bulkAddTagsToClickers(this.activeCmsId, emails, this.selectedTags)
                .then(function(result) {
                    self.bulkTagDialogVisible = false
                    self.$message.success(self.$t('Tags applied successfully'))
                })
                .catch(function(error) {
                    self.$message.error(_.get(error, 'message', 'Error applying tags'))
                })
        }
    }
}
</script>

<style lang="sass" scoped>
.url-clicks-content
    min-height: 200px

.url-stats-view
    min-height: 150px

.url-link
    color: #409eff
    text-decoration: none
    display: inline-flex
    align-items: center
    gap: 4px
    max-width: 100%

    &:hover
        text-decoration: underline

    .url-text
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    i
        flex-shrink: 0
        font-size: 12px
        color: #909399

.click-count
    font-weight: 600
    color: #e6a23c

.clicker-cell
    display: inline-flex
    align-items: center
    gap: 8px
    cursor: pointer
    padding: 4px 8px
    border-radius: 4px
    transition: background-color 0.2s

    &:hover
        background-color: #ecf5ff

    .clicker-count
        font-weight: 600
        color: #409eff

    .clicker-icon
        font-size: 11px
        color: #409eff

.summary
    display: flex
    justify-content: flex-end
    gap: 24px
    margin-top: 16px
    padding-top: 16px
    border-top: 1px solid #e4e7ed

    .summary-item
        display: flex
        gap: 8px

        .summary-label
            color: #909399
            font-size: 13px

        .summary-value
            font-weight: 600
            color: #303133
            font-size: 13px

.empty-state
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    padding: 40px 20px
    color: #909399

    i
        font-size: 48px
        margin-bottom: 16px

    p
        margin: 0
        font-size: 14px

.clickers-view
    min-height: 150px

.clickers-url-header
    margin-bottom: 16px
    padding: 12px 16px
    background-color: #f5f7fa
    border-radius: 4px

    .label
        color: #909399
        margin-right: 8px

    .url
        color: #303133
        font-weight: 500
        word-break: break-all

.pagination-footer
    margin-top: 16px
    display: flex
    justify-content: center

.bulk-tag-content
    padding: 0 10px

.dialog-footer
    display: flex
    justify-content: flex-end

.dialog-footer-spread
    display: flex
    justify-content: space-between
    width: 100%
</style>
